import {
  Alert,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useState } from 'react'
import {
  useCompanies,
  useOrderLimits,
  useRemoveOrderLimit,
  useSaveOrderLimit,
  useServiceTypes,
  useSessionTypes,
} from '@/api/hooks'
import type { OrderLimitView } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { ErrorBanner } from '@/components/common'

const BLANK: OrderLimitView = {
  id: '',
  serviceType: '',
  sessionCode: '',
  maxPax: null,
  maxOrders: null,
  active: true,
}

/** Empty is no ceiling, which is not the same as a ceiling of nothing. */
const asField = (value: number | null) => (value == null ? '' : String(value))
const asStored = (value: string) => (value.trim() === '' ? null : Number(value))

/**
 * What one sitting can take in a day.
 *
 * <p>A kitchen has a ceiling and so does a floor: so many covers it can cook
 * and serve for a lunch, and so many separate jobs it can run whatever the
 * covers come to. Twenty takeaway orders of thirty each is twenty deliveries
 * out of one door, which is a different constraint from the six hundred
 * meals.</p>
 *
 * <p>Both are set per line of business and per sitting, because they are
 * different questions for each: an outdoor lunch and a counter breakfast do not
 * share a kitchen's worth of capacity. Anything left blank has no ceiling.</p>
 *
 * <p>An order that would go over is refused outright rather than sent for
 * approval. A ceiling says what the kitchen can physically do on the day, and
 * an order taken over it is not a discount somebody can decide to live with -
 * it is meals that will not be cooked. Where the office wants the work anyway,
 * the ceiling is theirs to raise.</p>
 */
export function OrderLimitsTab() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const ownCompanyId = useAuthStore((state) => state.user?.companyId)
  const [companyId, setCompanyId] = useState<string | undefined>(undefined)
  const target = companyId ?? ownCompanyId
  const { data: companies } = useCompanies()
  const canPickCompany = can('platform.admin') && (companies ?? []).length > 1

  const { data: services } = useServiceTypes({ companyId, includeInactive: true })
  const { data: sittings } = useSessionTypes({ companyId, includeInactive: true })
  const { data: limits, error } = useOrderLimits(companyId)
  const save = useSaveOrderLimit()
  const remove = useRemoveOrderLimit()

  const [rows, setRows] = useState<OrderLimitView[]>([])
  const writable = canWrite('config')

  useEffect(() => {
    setRows((limits ?? []).map((row) => ({ ...row })))
  }, [limits])

  const update = (index: number, patch: Partial<OrderLimitView>) =>
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...patch } : row)))

  return (
    <>
      <ErrorBanner error={error ?? save.error ?? remove.error} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {canPickCompany ? (
            <TextField
              select
              size="small"
              label="Company"
              value={target ?? ''}
              onChange={(event) => setCompanyId(event.target.value)}
              sx={{ minWidth: 220 }}
              inputProps={{ 'aria-label': 'Company' }}
            >
              {(companies ?? []).map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            What one session can take in a day. Anything not listed here takes as much as comes.
          </Typography>
        </Stack>
        {writable ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setRows((current) => [...current, { ...BLANK }])}
          >
            New limit
          </Button>
        ) : null}
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        An order that would go over is refused, and the refusal says which session, what the day
        would come to and what the ceiling is. Leave a box empty for no ceiling.
      </Alert>

      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <TableCell width={220}>Line of business</TableCell>
            <TableCell width={200}>Session</TableCell>
            <TableCell width={150}>Covers a day</TableCell>
            <TableCell width={150}>Orders a day</TableCell>
            <TableCell align="right" width={120}>
              &nbsp;
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.id || `new-${index}`}>
              <TableCell>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={row.serviceType}
                  disabled={!writable || Boolean(row.id)}
                  onChange={(event) => update(index, { serviceType: event.target.value })}
                  inputProps={{ 'aria-label': `Line of business ${index + 1}` }}
                >
                  {(services ?? []).map((service) => (
                    <MenuItem key={service.code} value={service.code}>
                      {service.name}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={row.sessionCode}
                  disabled={!writable || Boolean(row.id)}
                  onChange={(event) => update(index, { sessionCode: event.target.value })}
                  inputProps={{ 'aria-label': `Session ${index + 1}` }}
                >
                  {(sittings ?? []).map((sitting) => (
                    <MenuItem key={sitting.code} value={sitting.code}>
                      {sitting.name}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell>
                <Tooltip title="Total covers across every order for that session on one day">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="No ceiling"
                    value={asField(row.maxPax)}
                    disabled={!writable}
                    onChange={(event) => update(index, { maxPax: asStored(event.target.value) })}
                    inputProps={{ 'aria-label': `Covers a day ${index + 1}`, min: 1 }}
                  />
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title="Separate jobs for that session on one day, whatever the covers come to">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="No ceiling"
                    value={asField(row.maxOrders)}
                    disabled={!writable}
                    onChange={(event) => update(index, { maxOrders: asStored(event.target.value) })}
                    inputProps={{ 'aria-label': `Orders a day ${index + 1}`, min: 1 }}
                  />
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                {writable ? (
                  <>
                    <Button
                      size="small"
                      disabled={save.isPending || !row.serviceType || !row.sessionCode}
                      onClick={() =>
                        save.mutate({
                          id: row.id || undefined,
                          companyId,
                          serviceType: row.serviceType,
                          sessionCode: row.sessionCode,
                          maxPax: row.maxPax,
                          maxOrders: row.maxOrders,
                          active: true,
                        })
                      }
                    >
                      Save
                    </Button>
                    {row.id ? (
                      <Tooltip title="Lift the ceiling entirely">
                        <IconButton
                          size="small"
                          onClick={() => remove.mutate({ id: row.id, companyId })}
                          aria-label={`Remove the limit ${index + 1}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Nothing is limited. Every session takes as much work as comes.
        </Typography>
      ) : null}
    </>
  )
}
